import { Injectable } from '@angular/core';
import PouchDB from 'pouchdb';
import PouchFind from 'pouchdb-find';
import { FisheryNotice } from 'src/app/data-models/fishery-notice.model';
import { Trip } from 'src/app/data-models/trip-models/trip.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { encoders, Whirlpool } from 'whirlpool-hash';
import { AuthenticationService } from '../authentication.service';
import { ValidationService } from '../validation.service';
import { ConflictResolverService } from '../conflict-resolver.service';
import { environment } from '../../../environments/environment';
import { FinOption } from '../../reports/start-report/start-report.component';

PouchDB.plugin(PouchFind);

@Injectable({
  providedIn: 'root'
})
export class UserStorageService {

  private static userStorage: PouchDB.Database; // user data (read and write)
  public static noticeMetadata: FisheryNotice[] = [];
  public static currUser: UserData = new UserData();
  public static adminUser?: UserData; //Only filled if an admin is logged in
  public static currTrip: Trip = new Trip();
  public static lastTrip: Trip = new Trip();
  public static prevTrips: Trip[] = [];
  public static error: string = ''; // should probably get rid of this and correctly handle promises instead
  private static resolve: boolean = true;

  // how long a report may sit "Awaiting response" before the app gives up on FOS, and the
  // confirmation number written in its place (see flagUnresponsiveReports)
  private static readonly FOS_RESPONSE_TIMEOUT_MS: number = 24 * 60 * 60 * 1000;
  public static readonly FOS_UNRESPONSIVE_MESSAGE: string = 'Error - FOS Unresponsive. Please reopen and resubmit this report';

  constructor() {
    
  }

  /*  ---------- PouchDB Initialization Methods  ---------- */


  // get a specific pouch based on the pouch ID
  public static getPouchDB(pouchID: string): PouchDB.Database {

    return new PouchDB(pouchID, { revs_limit: 500, auto_compaction: true });

  }

  // get user data from the user PouchDB
  public static getUserPouch(username: string): void {
    this.userStorage = this.getPouchDB('user-' + username);
  }


  public static getRemote(): PouchDB.Database {
    let apiKey = this.adminUser?.apiKey ?? this.currUser.apiKey;
    let userDbName = this.currUser.userEmail.replace(/@/g, "_").replace(/%/g, "_").replace(/\./g, "_").replace(/\+/g, "_") + "_trips";
    return new PouchDB(`https://${apiKey}@${environment.databaseName}/${userDbName}`);
  }


  /*  ---------- Get User Methods  ---------- */

  public static checkAdminUser(email: string, userData?: UserData, isAdmin: boolean = false): Promise<boolean> {
    this.getUserPouch(email);
    return new Promise<boolean>((res) => {
      this.userStorage.get('_local/admin_user').then((doc: any) => {
        this.adminUser = UserData.transformAnonymous(doc.user);
        this.adminUser.adminReplicated = doc.replicatedUser;
        if (userData) {
          this.adminUser.apiKey = userData.apiKey;
          doc.user = userData;
          this.userStorage.put(doc).then(() => res(true));
        } else res(true);
      }).catch((err: { name: string }) => {
        if (err.name == 'not_found' && isAdmin) {
          this.adminUser = userData;
          this.userStorage.put(
            {
              _id: '_local/admin_user',
              user: userData,
              replicatedUser: ''
            }
          ).then(() => res(true));
        } else {
          res(false);
        }
      });
    });
    
  }

  // get current user or create new user in PouchDB online
  //isAdmin will only be true if an admin user is logging in
  public static getUserOnline(email: string, userData?: UserData, isAdmin: boolean = false): Promise<boolean> {
    this.error = '';

    return new Promise<boolean>((res) => {
      this.checkAdminUser(email, userData, isAdmin).then((result) => {
        if (result && this.adminUser!.adminReplicated == '') res(true);
        else {
          this.userStorage.get('user').then((doc: any) => {
            console.log('got user doc')
            this.validateGetUserOnline(doc, email);
            if (userData) {
              this.currUser.apiKey = userData.apiKey;
              let userbooks = [];
              //Basic idea: For each logbook received by the server, user already has it or it's a new logbook
              //The logbook is considered registered if the user has a vessel matching the vessel linked to the logbook
              //Assigning the vessel to the logbook prevents smaller issues from popping up in other places (i.e. on the start report when selecting a logbook)
              for (let logbook of userData.logbooks) {
                let vesselMatch = this.currUser.vessels.find(vessel => vessel.vesselRegistrationNumber == logbook.vessel.vesselRegistrationNumber);
                logbook.isRegistered = !!vesselMatch;
                logbook.vessel = vesselMatch ?? logbook.vessel;
                userbooks.push(logbook);
              }
              this.currUser.logbooks = userbooks;
              this.saveUser(this.currUser).then(() => res(true));
            } else res(true);
          }).catch((err: { name: string }) => {
            console.log(err)
            this.displayError(err);
            if (err.name === 'not_found') {
              let newUser: UserData = userData ?? new UserData(email);
              newUser.apiKey = userData?.apiKey ?? '';
              newUser.currHomepage = userData ? userData.currHomepage : ValidationService.setHomepage();
              this.createNewUser(newUser, 'user').then(() => {
                res(this.resolve);
              });
            }
            else {
              res(false);
            }
          });
        }
      });
    });
  }


  // ensure that user data exists
  private static validateGetUserOnline(doc: any, email: string): void {

    let user: any = doc.user;
    let thisUser: UserData = new UserData();

    if (!user || !user.userEmail) {
      console.log("User data corrupted... creating new user data");
      thisUser.userEmail = email;
      thisUser.currHomepage = ValidationService.setHomepage();
      AuthenticationService.saveCurrUsername(thisUser.userEmail);
      this.currUser = thisUser;
      doc.user = thisUser;
      doc.lastUpdate = new Date();
      this.userStorage.put(doc).then(() => {
        return;
      }).catch((err: { name: string }) => {
        this.displayError(err);
      });
    }
    
    // get user data
    this.parseUserData(user);
    
  }

  // get current user or create new user in PouchDB
  public static getUserOffline(email: string, password: string = ''): Promise<boolean> {

    return new Promise<boolean>((res) => {

      if (this.isExistingUser(email)) {
        let docID: string = 'user';
        this.error = '';
        this.getUserPouch(email);

        this.userStorage.get(docID).then((doc: any) => {

          //Check the user pasword against the stored hash if necessary
          let hash = encoders.toBase64(new Whirlpool().getHash(password).toString());
          if (password && hash != doc.user.passwordHash) res(false);
          this.validateGetUserOffline(doc);
          res(true);

        }).catch((err: { name: string }) => {
          this.displayError(err);
          if (this.error === 'not_found') {
            this.error = 'User not stored on device';
            console.log("User not found");
            this.currUser = new UserData();
          }
          res(false);
        });
      }
      else {
        this.error = 'User email not saved while online';
        console.log(this.error);
        res(false);
      }
    });

  }

  // ensure that user data exists
  private static validateGetUserOffline(doc: any): void {

    let user: any = doc.user;

    if (!user || !user.userEmail) {
      console.log("User data corrupted");
      this.currUser = new UserData();
      return;
    }

    // get user data
    this.parseUserData(user);

  }


  /*  ---------- Save User Methods ---------- */


  // save user data to PouchDB
  public static saveUser(userData: UserData, sync: boolean = false): Promise<boolean> {
    let email: string = userData.userEmail;
    let docID: string = 'user';
    this.error = '';
    this.getUserPouch(email);
    
    return new Promise<boolean>((res) => {
      if (this.adminUser) res(false);
      else {
        this.userStorage.get(docID).then((doc: any) => {
          this.validateSaveUser(doc, userData).then(() => {
            if (sync)
              this.userStorage.replicate.to(this.getRemote()).on('complete', () => res(true)).on('error', (err: any) => {
                console.log('Could not sync data to Couch: ' + err);
                res(false);
              });
            else res(true);
          }).catch((err: { name: string }) => {
            console.log("Error:", err.name);
            res(false);
          });
        }).catch((err: { name: string }) => {
          this.displayError(err);
          if (this.error === 'not_found') {
            this.createNewUser(userData, docID).then(() => {
              res(this.resolve);
            });
          }
          else {
            res(false);
          }
        });
      }
      
    });

  }

  // ensure that user data exists
  private static validateSaveUser(doc: any, userData: UserData): Promise<boolean> {

    AuthenticationService.saveCurrUsername(userData.userEmail);
    this.currUser = userData;
    doc.user = userData;
    doc.lastUpdate = new Date();

    return new Promise<boolean>((res) => {
      this.userStorage.put(doc).then(() => {
        res(true);
      }).catch((err: { name: string }) => {
        this.displayError(err);
        res(false);
      });
    });

  }

  //If the user password has been reset, we use this method to check if the local stored profile of that user has the new password
  //This is important to ensure offline login for the user still works 
  public static checkPasswordHashMatch(password: string): Promise<boolean> {
    let hash = encoders.toBase64(new Whirlpool().getHash(password).toString());
    
    return new Promise<boolean>((res, rej) => {
      if (this.currUser.passwordHash != hash) {
        this.currUser.passwordHash = hash;
        this.saveUser(this.currUser).then((result: boolean) => res(result));
      } else {
        res(true);
      }
    })
  }


  /*  ----------  User Helper Methods  ---------- */


  // create new user due to user not existing upon login
  private static createNewUser(newUser: UserData, docID: string): Promise<boolean> {

    console.log("User not found, creating user...");

    let username: string = newUser.userEmail;
    this.error = '';
    AuthenticationService.saveCurrUsername(username);
    AuthenticationService.addNewUsername(username);
    this.currUser = newUser;

    return new Promise<boolean>((res) => {
      this.userStorage.put({
        _id: docID,
        user: newUser,
        lastUpdate: new Date()
      }).then(() => {
        this.resolve = true;
        res(true);
      }).catch((err: { name: string }) => {
        this.displayError(err);
        this.resolve = false;
        res(false);
      });
    });

  }

  // check if user is an existing user
  //Rather than just check if user is stored, check their password against hashed version
  private static isExistingUser(email: string): boolean {

    let allUsernames: string[] = AuthenticationService.getAllUsernames();
    for (let username of allUsernames) {
      if (email === username) {
        return true;
      }
    }
    return false;

  }

  // parse all of the possible data from PouchDB into a UserData object
  private static parseUserData(user: any): void {
    
    this.currUser = UserData.transformAnonymous(user);
    AuthenticationService.saveCurrUsername(this.currUser.userEmail);
  }


  /*  ---------- Get Trip Methods ---------- */


  // get the user's current trip
  //
  // Nothing in the schema enforces one active trip. A finished trip is archived by a
  // fire-and-forget save on the no-trip homepage, a deliberate switch (setActiveTrip) has to touch
  // two documents, and isActive replicates, so a lost race or a two-device conflict can leave more
  // than one document flagged active. Take all of them and pick a winner rather than whichever one
  // the index happened to return first -- that was ascending _id, i.e. the OLDEST trip.
  public static getCurrTrip(): Promise<boolean> {
    if (!this.userStorage) this.getUserPouch(this.currUser.userEmail);
    return new Promise<boolean>((res) => {
      this.userStorage.createIndex({
        index: {
          fields: ['_id', 'isActive']
        }
      }).then(() => {
        this.userStorage.find({
          selector: {
            _id: { $gt: 'trip-', $lt: 'trip-\uffff' },
            isActive: true
          }
        }).then((result: any) => {
          let docs: any[] = result.docs ?? [];
          if (docs.length > 0) {
            let winner: any = this.mostRecentlyActivated(docs);
            this.currTrip = Trip.transformAnonymous(winner.trip);
            this.archiveStrayActiveTrips(docs, winner).then(() => res(true));
          }
          else {
            res(true);
          }
        }).catch((err: { name: string }) => {
          this.displayError(err);
          res(false);
        });
      }).catch((err: { name: string }) => {
        this.displayError(err);
        res(false);
      });
    });

  }

  // pick the trip that most recently became the active one. activatedAt is stamped on the
  // transition into active (validateSaveTrip), so a deliberate switch to an older trip outranks a
  // stale device re-flagging a newer one. Documents saved before the stamp existed tie at 0 and
  // fall back to the start-date ranking, which is itself deterministic.
  private static mostRecentlyActivated(docs: any[]): any {

    const activatedAt = (doc: any): number => {
      let activated: number = new Date(doc?.activatedAt).getTime();
      return isNaN(activated) ? 0 : activated;
    };

    return docs.reduce((latest: any, doc: any) => {
      let difference: number = activatedAt(doc) - activatedAt(latest);
      if (difference > 0) return doc;
      if (difference === 0) return this.mostRecentlyStarted([latest, doc]);
      return latest;
    });

  }

  // clear isActive on every active document that lost the tie-break, so the next read is
  // unambiguous. Written with a bare put rather than saveTrip: only the flag changes, so the
  // FOS-preserving merge has nothing to do, and getCurrTrip runs on every guarded navigation --
  // it must not reach the network. The change rides up on the next sync.
  private static async archiveStrayActiveTrips(docs: any[], winner: any): Promise<void> {

    if (this.adminUser) return; // an admin reading someone else's records must not rewrite them

    for (const doc of docs) {
      if (doc._id === winner._id) continue;
      try {
        doc.isActive = false;
        await this.userStorage.put(doc);
        console.log('Archived stray active trip: ' + doc._id);
      } catch (err: any) {
        console.log('Could not archive stray active trip ' + doc._id + ': ' + err);
      }
    }

  }

  // get the user's most recently started trip
  //
  // Trip doc IDs are 'trip-YYYY-MM-DD-<nanoid>', so sorting by _id only orders trips by the day
  // they were started -- every trip started on the same day ties and comes back in nanoid order,
  // which is random. Rank the candidates by their start date instead so this really is the last
  // trip. Trips that are still flagged active are included too: the previously completed trip is
  // only archived (isActive: false) by a fire-and-forget save on the no-trip homepage, so a user
  // who taps straight through to a new start report can get here before that save lands.
  public static getLastTrip(): Promise<boolean> {

    if (!this.userStorage) this.getUserPouch(this.currUser.userEmail);

    return new Promise<boolean>((res) => {
      this.userStorage.find({
        selector: {
          _id: { $gt: 'trip-', $lt: 'trip-\uffff' }
        }
      }).then((result: any) => {
        let docs: any[] = result.docs ?? [];
        if (docs.length > 0) {
          this.lastTrip = Trip.transformAnonymous(this.mostRecentlyStarted(docs).trip);
          res(true);
        }
        else {
          console.log("No previous trips");
          res(true);
        }
      }).catch((err: { name: string }) => {
        this.displayError(err);
        res(false);
      });
    });

  }

  // pick the trip doc with the latest start date, falling back to the doc ID when a trip has no
  // usable start date (so the result stays stable rather than depending on the find() ordering)
  private static mostRecentlyStarted(docs: any[]): any {

    const startedAt = (doc: any): number => {
      let started: number = new Date(doc?.trip?.startFishingReport?.startDate).getTime();
      return isNaN(started) ? 0 : started;
    };

    return docs.reduce((latest: any, doc: any) => {
      let difference: number = startedAt(doc) - startedAt(latest);
      if (difference > 0) return doc;
      if (difference === 0 && doc._id > latest._id) return doc;
      return latest;
    });

  }

  // get all of the user's trips
  public static getAllPrevTrips(): Promise<boolean> {

    return new Promise<boolean>((res) => {
      this.userStorage.createIndex({
        index: {
          fields: ['_id', 'isActive']
        }
      }).then(() => {
        this.userStorage.find({
          selector: {
            _id: { $gt: 'trip-', $lt: 'trip-\uffff' },
            isActive: false
          },
          sort: [{ '_id': 'desc' }]
        }).then((result: any) => {
          let docs: any = result.docs;
          if (docs.length > 0) {
            this.prevTrips = Trip.transformAnonArray(docs);
            res(true);
          }
          else {
            console.log("No previous trips");
            res(true);
          }
        }).catch((err: { name: string }) => {
          this.displayError(err);
          res(false);
        });
      }).catch((err: { name: string }) => {
        this.displayError(err);
        res(false);
      });
    });

  }


  /*  ---------- Save Trip Methods ---------- */

/**
 * Save (and optionally sync) a Trip document while preserving any existing
 * FOS-issued confirmation numbers on the server, regardless of revision tree shape.
 **/
  public static async saveTrip( currTrip: Trip, activeTrip: boolean = true, syncTrip: boolean = true ): Promise<boolean> {
    const docID: string = 'trip-' + currTrip.tripID;
    if (this.adminUser) syncTrip = false;
    if (!this.userStorage) this.getUserPouch(this.currUser.userEmail);

    const remote = this.getRemote();

    // Targeted pre-pull so local has the remote leaves for this doc
    const prePull = async () => {
      if (!syncTrip) return;
      await new Promise<void>((ok) => {
        this.userStorage
          .replicate.from(remote, { doc_ids: [docID] })
          .on('complete', () => ok())
          .on('error', () => ok()); // don't block on errors/offline
      });
    };

    try {
      await prePull();

      // Collect ALL prior trips (remote & local leaves) to preserve FOS from
      const prevTrips: any[] = [];

      // 1) Remote leaves
      if (syncTrip) {
        try {
          const remoteLeaves: any[] = await remote.get(docID, { open_revs: 'all' });
          for (const row of remoteLeaves) {
            const t = row?.ok?.trip;
            if (t) prevTrips.push(t);
          }
        } catch { }
      }

      // 2) Local leaves
      try {
        const localLeaves: any[] = await this.userStorage.get(docID, { open_revs: 'all' });
        for (const row of localLeaves) {
          const t = row?.ok?.trip;
          if (t) prevTrips.push(t);
        }
      } catch {  }

      // Get current local winning doc (or create a stub if not found)
      let doc: any;
      try {
        doc = await this.userStorage.get(docID);
      } catch (err: any) {
        if (err?.name === 'not_found') {
          doc = { _id: docID };
        } else {
          throw err;
        }
      }

      // Pass prevTrips to the validator/merger
      const ok = await this.validateSaveTrip(doc, currTrip, activeTrip, syncTrip, prevTrips);
      return ok;
    } catch (e) {
      this.displayError(e);
      return false;
    }
  }

/**
 * Merge-and-save helper that enforces the "preserve existing FOS confirmation numbers"
 * rule, then persists the result locally and (optionally) syncs.
 **/
  private static async validateSaveTrip(doc: any, trip: Trip, activeTrip: boolean, syncTrip: boolean, prevTrips: any[] = []): Promise<boolean> {
    const existingTrip: Trip | null = doc?.trip ? Trip.transformAnonymous(doc.trip) : null;
    const PLACEHOLDER_CN = new Set(['NOT SUBMITTED','INCOMPLETE','AWAITING RESPONSE']);
    const up = (s?: string) => (s ?? '').trim().toUpperCase();

    // 0 = empty/unknown, 1 = placeholder, 2 = error text, 3 = FOS
    const cnRank = (cn?: string) => {
      const u = up(cn);
      if (!u) return 0;
      if (u.startsWith('FOS')) return 3;
      if (PLACEHOLDER_CN.has(u)) return 1;
      return 2; // everything else is an error message
    };

    // recursively preserve FOS confirmation/error responses from all existing revisions passed in prevTrips
    function preserveFOS(prev: any, next: any) {
      const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v);

      if (Array.isArray(prev) && Array.isArray(next)) {
        // Align array items by a stable key if present, else by index
        const key = ['internalId', 'reportID', 'id'].find(k =>
          next.every(it => !it || typeof it !== 'object' || k in it)
        );

        if (key) {
          const prevByKey = new Map(
            prev.filter(isObj).map((o: any) => [o[key], o])
          );
          for (const item of next) {
            if (isObj(item) && prevByKey.has(item[key])) {
              preserveFOS(prevByKey.get(item[key]), item);
            }
          }
        } else {
          for (let i = 0; i < next.length; i++) {
            preserveFOS(prev[i], next[i]);
          }
        }
        return;
      }

      if (isObj(prev) && isObj(next)) {
        for (const k of Object.keys(next)) {
          if (k === 'confirmationNumber') {
            const oldVal = typeof prev[k] === 'string' ? prev[k] : undefined;
            const newVal = typeof next[k] === 'string' ? next[k] : undefined;

            // Only copy when prev is strictly more authoritative.
            let preserve = !!oldVal && cnRank(oldVal) > cnRank(newVal);

            // Exception: a stale error response (rank 2) vs a fresh "Awaiting response"
            // placeholder (rank 1). A deliberate resubmission (reopenReport) sets a newer
            // completionDate and SHOULD replace the old error, so don't preserve the error
            // when the incoming report is the newer of the two.
            if (preserve && cnRank(oldVal) === 2 && cnRank(newVal) === 1) {
              const oldT = new Date(prev.completionDate ?? 0).getTime();
              const newT = new Date(next.completionDate ?? 0).getTime();
              if (newT > oldT) preserve = false;
            }

            if (preserve) next[k] = oldVal;
          } else if (k === 'emailedAt') {
            // the backend stamps emailedAt on the Cloudant copy when a report email is actually
            // sent; an in-memory trip that predates the stamp must not erase it on save, or the
            // backend loses its duplicate-send protection for that report
            const oldVal = typeof prev[k] === 'string' ? prev[k] : undefined;
            if (oldVal && !next[k]) next[k] = oldVal;
          } else {
            preserveFOS(prev[k], next[k]);
          }
        }
      }
    }

    // Build the merged trip: start from incoming, then preserve FOS responses from all prior sources
    const mergedTrip: any = JSON.parse(JSON.stringify(trip));
    for (const prev of prevTrips) preserveFOS(prev, mergedTrip); // remote + local leaves first
    if (existingTrip) preserveFOS(existingTrip, mergedTrip);     // then local winning (typed) if present

    // Deletions must survive the merge. Tombstones are additive across every revision and
    // any report they name is dropped no matter which revision reintroduced it -- otherwise a stale
    // in-memory trip (or a second device's leaf) quietly resurrects a report the user deleted.
    const tombstones = new Set<string>(
      [...prevTrips, existingTrip, mergedTrip]
        .flatMap((t: any) => Array.isArray(t?.deletedReportIds) ? t.deletedReportIds : [])
    );
    mergedTrip.deletedReportIds = [...tombstones];
    for (const listName of Trip.reportListNames) {
      if (Array.isArray(mergedTrip[listName]))
        mergedTrip[listName] = mergedTrip[listName].filter((r: any) => !tombstones.has(r?.internalId));
    }

    // Commit merged content.
    // activatedAt records when this trip BECAME the active one -- only on the transition, so the
    // stamp keeps meaning "chosen most recently" instead of drifting forward with every report
    // saved against an already-active trip. getCurrTrip ranks competing active documents by it.
    if (activeTrip && doc.isActive !== true) doc.activatedAt = new Date().toISOString();
    doc.isActive = activeTrip;
    doc.trip = mergedTrip;

    // Keep in-memory state aligned with what was just persisted, but do it in place instead of
    // re-pointing currTrip at the merged clone. Two things depend on that:
    //   - identity: callers hold this trip (and the report objects inside it) across the save, so
    //     swapping the graph out leaves them mutating orphans and saving stale data
    //   - type: the clone is plain JSON, so every Trip/model method is gone and every Date is a
    //     string, while the declared type still claims Trip -- the compiler cannot catch the misuse
    if (activeTrip) {
      preserveFOS(mergedTrip, trip); // forward-apply the merged FOS responses onto the live trip

      for (const listName of Trip.reportListNames) { // and drop whatever the tombstones removed
        const liveReports: any[] = (trip as any)[listName];
        if (!Array.isArray(liveReports)) continue;
        for (let i = liveReports.length - 1; i >= 0; i--)
          if (tombstones.has(liveReports[i]?.internalId)) liveReports.splice(i, 1);
      }
      (trip as any).deletedReportIds = [...tombstones];

      // a caller that handed us a degraded trip gets a rehydrated one rather than keeping it plain
      this.currTrip = trip instanceof Trip ? trip : Trip.transformAnonymous(mergedTrip);
    }

    try {
      await this.userStorage.put(doc);

      if (!syncTrip) return true;

      const pushed = await new Promise<boolean>((res) => {
        this.userStorage
          .sync(this.getRemote())
          .on('complete', () => { console.log('Synced trip data with Couch (push+pull)'); res(true); })
          .on('denied', (e: any) => { console.log('Sync denied:', e); res(true); })
          .on('error', (e: any) => { console.log('Sync failed:', e); res(false); });
      });

      return pushed;
    } catch (err: any) {
      this.displayError(err);
      return false;
    }
  }

/**
 * Remove a report from the active trip and persist the removal.
 *
 * Deliberately works from the live static trip instead of a handle passed in by the caller:
 * validateSaveTrip re-points currTrip at a fresh clone after every save, so a component that
 * captured the trip in ngOnInit is holding a stale object. Splicing one and saving the other
 * writes the report straight back.
 *
 * Also records the id as a tombstone so neither the save-time merge nor conflict resolution can
 * re-add the report from a revision that still has it.
 **/
  public static deleteTripReport(selectReports: (trip: any) => any[], internalId: string): Promise<boolean> {

    const trip: any = this.currTrip;
    const reports: any[] = selectReports(trip) ?? [];
    const index: number = reports.findIndex((report: any) => report?.internalId === internalId);

    if (index > -1) {
      const removed: any = reports.splice(index, 1)[0];
      // clear the report's outstanding-submission marker, otherwise the records page keeps
      // counting (and warning about) a report that no longer exists
      if (removed?.completionDate) {
        const completedAt: number = new Date(removed.completionDate).getTime();
        trip.unsubmittedReports = (trip.unsubmittedReports ?? [])
          .filter((date: Date) => new Date(date).getTime() !== completedAt);
      }
    }

    if (!Array.isArray(trip.deletedReportIds)) trip.deletedReportIds = [];
    if (!trip.deletedReportIds.includes(internalId)) trip.deletedReportIds.push(internalId);

    return this.saveTrip(trip);

  }

/**
 * Make an already-archived trip the active one again (the records page trip switcher).
 *
 * Two documents change state and the order matters: every other active document is archived first,
 * then the chosen trip is activated, because saveTrip only re-points currTrip when it saves an
 * active trip. Doing it the other way round would leave currTrip pointing at the trip being
 * retired.
 *
 * The outgoing trip is read out of the pouch rather than taken from currTrip. currTrip can be a
 * placeholder Trip (getCurrTrip leaves it untouched when nothing is active, and a fresh Trip still
 * has a generated tripID, so it does not read as empty), and there can be more than one active
 * document after a lost archive race or a device conflict -- all of them have to be cleared.
 *
 * Callers still own UserData.currHomepage. isActive alone does not move the user: the route guard
 * authorizes every report route off currHomepage and LOGS THE USER OUT when it says no, and the
 * records page shows its "Current Trip" section on the same field. Set it to 'active-trip-home'
 * and saveUser, or the switch strands the user.
 *
 * A false return means the Cloudant sync failed, not that the switch failed -- saveTrip has
 * already committed locally by then, which is the normal offline-at-sea case.
 **/
  public static async setActiveTrip(trip: Trip): Promise<boolean> {

    if (this.adminUser) return false; // an admin reading someone else's records must not rewrite them
    if (!this.userStorage) this.getUserPouch(this.currUser.userEmail);

    const docID: string = 'trip-' + trip.tripID;
    let synced: boolean = true;

    try {
      const result: any = await this.userStorage.find({
        selector: {
          _id: { $gt: 'trip-', $lt: 'trip-\uffff' },
          isActive: true
        }
      });

      for (const doc of (result?.docs ?? [])) {
        if (doc._id === docID) continue;
        console.log('Archiving active trip: ' + doc._id);
        synced = await this.saveTrip(Trip.transformAnonymous(doc.trip), false) && synced;
      }
    } catch (err: any) {
      this.displayError(err);
      return false; // the outgoing trip is still active, so don't activate a second one
    }

    return await this.saveTrip(trip, true) && synced;

  }


  // submit current trip and put it in as a previous trip
  public static submitTrip(): void {

    this.lastTrip = this.currTrip;
    this.currTrip = new Trip();

  }


  /*  ---------- Update Confirmation Numbers ---------- */


  // This method sync's with CouchDB which will pull any confirmation numbers
  // that have been received from FOS into the app
  public static updateConfirmationNumbers(): Promise<boolean> {

    return new Promise<boolean>((res) => {
      this.userStorage.replicate.from(this.getRemote()).on('complete', () => {
        console.log('Synced trip data from Couch');
        ConflictResolverService.resolveConflicts(this.userStorage)
          .then(() => this.flagUnresponsiveReports())
          .then(() => res(true))
          .catch(() => res(false));
      }).on('error', (err: any) => {
        console.log('Could not sync trip data from Couch: ' + err);
        res(false);
      });             
    });
  }

/**
 * Give up on reports FOS has stopped answering.
 *
 * FOS normally answers a report email within minutes, and its answer is what turns "Awaiting
 * response" into a confirmation number (backend addConfirmationNumber). When the answer never
 * arrives -- a dropped reply, a stalled ingestion pipeline -- nothing else in the system ever
 * clears that status, so the report sits in limbo and the user has no way to tell it apart from
 * one that is still in flight. A day is well past any legitimate wait, so replace the placeholder
 * with an error telling the user to resubmit.
 *
 * The message is deliberately written into confirmationNumber rather than tracked separately:
 * every consumer already reads report state from that field, so it makes getReportStatus return
 * "Error with submission", exposes the report component's Reopen button, and makes the backend
 * treat the eventual re-send as a deliberate resubmission instead of a suppressed duplicate.
 *
 * Runs over every trip in the pouch, not just the active one, because the records page shows the
 * status of past trips' reports too.
 **/
  private static async flagUnresponsiveReports(): Promise<void> {

    if (this.adminUser) return; // an admin reading someone else's records must not rewrite them

    const cutoff: number = Date.now() - this.FOS_RESPONSE_TIMEOUT_MS;

    const isUnresponsive = (report: any): boolean => {

      if (!report || typeof report !== 'object') return false;
      if (!(report.confirmationNumber ?? '').toLowerCase().includes('awaiting response')) return false;

      // no stamp means the email never actually went out (sendReportEmail holds reports back until
      // the start report is accepted), so FOS is not the one keeping this report waiting
      const emailedAt: number = new Date(report.emailedAt ?? '').getTime();
      if (isNaN(emailedAt) || emailedAt > cutoff) return false;

      // likewise a report completed again since its last send: that is a resubmission the backend
      // is still holding, and it will be re-emailed (and re-stamped) when the start report clears
      const completedAt: number = new Date(report.completionDate ?? '').getTime();
      return isNaN(completedAt) || completedAt <= emailedAt;

    };

    try {

      const result: any = await this.userStorage.find({
        selector: { _id: { $gt: 'trip-', $lt: 'trip-\uffff' } }
      });

      for (const doc of (result?.docs ?? [])) {

        const trip: any = doc?.trip;
        if (!trip) continue;

        const reports: any[] = [
          trip.startFishingReport,
          trip.endFishingReport,
          trip.cancelFishingReport,
          ...Trip.reportListNames.flatMap((listName: string) => Array.isArray(trip[listName]) ? trip[listName] : [])
        ];

        const unresponsive: any[] = reports.filter(isUnresponsive);
        if (!unresponsive.length) continue;

        unresponsive.forEach((report: any) => report.confirmationNumber = this.FOS_UNRESPONSIVE_MESSAGE);

        // saved through saveTrip rather than a bare put: it merges against the remote and local
        // leaves first, so a confirmation number sitting on a revision this replication has not
        // surfaced still wins over the error, and it pushes the error up to Cloudant, which is
        // where the backend reads report state from when the user resubmits
        console.log('FOS unresponsive for: ' + unresponsive.map((report: any) => report.reportID).join(', '));
        await this.saveTrip(Trip.transformAnonymous(trip), doc.isActive === true);

      }

    } catch (err: any) {
      console.log('Could not flag unresponsive reports: ' + err);
    }

  }

  // Pushes all local docs to Cloudant. The backend composes report emails from the
  // Cloudant copy of the trip, so trips saved while offline (or while sync was
  // failing) must be pushed up before their reports can be resubmitted.
  public static pushTripData(): Promise<boolean> {

    if (!this.userStorage) this.getUserPouch(this.currUser.userEmail);
    return new Promise<boolean>((res) => {
      this.userStorage.replicate.to(this.getRemote()).on('complete', () => {
        console.log('Pushed trip data to Couch');
        res(true);
      }).on('error', (err: any) => {
        console.log('Could not push trip data to Couch: ' + err);
        res(false);
      });
    });
  }

/* ------------ Admin sync user data ---------------  */


  public static adminSyncUserData(remote: PouchDB.Database): Promise<boolean> {
    return new Promise<boolean>((res) => {
      this.userStorage.destroy().then(() => {
        this.getUserPouch(this.adminUser!.userEmail);
        this.userStorage.put(
          {
            _id: '_local/admin_user',
            user: this.adminUser,
            replicatedUser: this.adminUser!.adminReplicated
          }
        ).then(() => {
          this.userStorage.replicate.from(remote).on('complete', () => {
              console.log('User data synced down');
              res(true);
            }).on('error', (err: any) => {
              console.log('Could not sync user data: ' + err);
              res(false);
            });           
          });        
      });                     
    });
  }


  /*  ---------- Fishery Notice Metadata Methods ---------- */


  // gets the list of the user's fishery notice metadata 
  // need to update the metadata doc, removing all notices that are archived and not saved
  public static getNoticeMetadata(): Promise<boolean> {

    let docID: string = 'fisheryNoticeMetadata';

    return new Promise<boolean>((res) => {
      this.userStorage.get(docID).then((doc: any) => {
      this.noticeMetadata = FisheryNotice.transformAnonArray(doc.noticeList);
        res(true);
      }).catch((err: { name: string }) => {
        this.displayError(err);
        if (this.error === 'not_found') {
          this.error = '';
          console.log("Creating read notification list...");
          let noticeMetadata: FisheryNotice[] = [];
          this.userStorage.put({
            _id: docID,
            noticeList: noticeMetadata
          }).then(() => {
            res(true);
          }).catch((err: { name: string }) => {
            this.displayError(err);
            res(true);
          });
        }
        else {
          res(true);
        }
      });
    });

  }

  // saves an updated list of fishery notice metadata to the user's pouch db
  public static async saveNoticeMetadata(notices: FisheryNotice[]) {

    let docID: string = 'fisheryNoticeMetadata';

    this.userStorage.get(docID).then((doc: any) => { // retreive doc
      doc.noticeList = notices; // update notice list
      this.userStorage.put(doc).then(() => { // put doc back in user storage
        console.log('Notice data saved successfully!');
      });
    });

  }


  /*  ---------- FIN Option Methods ---------- */

  public static getFinOptions(): Promise<FinOption[]> {
    return new Promise<FinOption[]>((resolve, reject) => {
      this.userStorage.get('finOptions').then((doc: any) => {
        resolve(doc.options);
      }).catch((err: any) => {
        if (err.name === 'not_found') {
          // Create a new document with the user's name & fin as first entry
          const defaultOptions: FinOption[] = [{ name: this.currUser.firstName + " " + this.currUser.lastName, fin: this.currUser.fin }];
          this.userStorage.put({
            _id: 'finOptions',
            options: defaultOptions
          }).then(() => resolve(defaultOptions))
            .catch((err2: any) => reject(err2));
        } else {
          reject(err);
        }
      });
    });
  }

  public static saveFinOptions(options: FinOption[]): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.userStorage.get('finOptions').then((doc: any) => {
        doc.options = options;
        this.userStorage.put(doc).then(() => resolve(true))
          .catch((err: any) => reject(err));
      }).catch((err: any) => {
        if (err.name === 'not_found') {
          this.userStorage.put({
            _id: 'finOptions',
            options: options
          }).then(() => resolve(true))
            .catch((err2: any) => reject(err2));
        } else {
          reject(err);
        }
      });
    });
  }



  //Remove a notice from the metadata document, we do this for archived notices that no longer appear in the app
  public static async removeNoticeMetadata(notice: FisheryNotice) {

    let docID: string = 'fisheryNoticeMetadata';

    this.userStorage.get(docID).then((doc: any) => { // retreive doc
      let notices = doc.noticeList as FisheryNotice[];
      doc.noticeList = notices.filter((fn: FisheryNotice) => fn.noticeNumber != notice.noticeNumber)
      this.userStorage.put(doc).then(() => { // put doc back in user storage
        console.log('Notice data removed successfully!');
      });
    });

  }


  /*  ---------- PouchDB Close Methods ---------- */


  // clear/close all data from current session
  public static closeStorage(isAdmin: boolean): Promise<boolean> {

    this.noticeMetadata = [];
    this.currUser = new UserData();
    this.currTrip = new Trip();
    this.lastTrip = new Trip();
    this.prevTrips = [];
    this.error = '';
    this.resolve = true;
    AuthenticationService.saveCurrUsername('');
    AuthenticationService.setLogin('');

    

    return new Promise<boolean>((res) => {
      if (isAdmin) {
        console.log('Destroying admin replication')
        this.userStorage.destroy();
      } else {
        this.userStorage.close().then(() => { // close the user's data pouch
          console.log('Closed user db');
          res(true);
        }).catch((err: { name: string }) => {
          console.log('Could not close user db: ' + err);
          this.displayError(err);
          res(false);
        });
      }
        
    });
    
  }


  /*  ---------- Error Handling Methods ---------- */


  // display provided error
  private static displayError(err: any): void {

    console.log(err);
    this.error = err.name;
    console.log("Error:", this.error);

  }

}
